import { nanoid } from "nanoid";
import { INTERACTIVE_CONTENT_TYPES } from "src/pages/experiences/constants/storyboard";
import {
  GatingConfigurationResponse,
  GatingTriggerType,
} from "src/types/experiences/storyboardSettings";
import {
  STORYBOARD_TYPES,
  StoryboardType,
} from "src/types/experiences/storyboardSettings/storyboard";

export const makeGatingUid = (): string => nanoid(6);

export const createGatingSeed = (): GatingConfigurationResponse => ({
  uid: makeGatingUid(),
  name: "Untitled Gating",
  enabled: false,
  optional: false,
  trigger: { type: "TIME", value: 10 },
  target: null,
});

export const createAutoGatingSeed = (): GatingConfigurationResponse => ({
  uid: makeGatingUid(),
  name: "Auto Gating",
  enabled: false,
  optional: false,
  smart: false,
  autoGating: true,
  trigger: null,
  target: null,
  type: "AUTO_GATING",
});

export const GATING_TRIGGER_TYPES = {
  TIME: "TIME",
  PERCENTAGE: "PERCENTAGE",
  INTERACTIVE_SECTION: "INTERACTIVE_SECTION",
  INTERACTIVE_DOWNLOAD: "INTERACTIVE_DOWNLOAD",
  EXIT_INTENT: "EXIT_INTENT",
  SMART: "SMART",
  AUTO_GATING: "AUTO_GATING",
  SPATIAL_SLIDE: "SPATIAL_SLIDE",
  FLIPBOOK_SLIDE: "FLIPBOOK_SLIDE",
} as const;

type TriggerOption = { value: GatingTriggerType; label: string };

const TIME_OPTION: TriggerOption = { value: GATING_TRIGGER_TYPES.TIME, label: "Time" };
const PERCENTAGE_OPTION: TriggerOption = { value: GATING_TRIGGER_TYPES.PERCENTAGE, label: "Percentage" };
const SECTION_OPTION: TriggerOption = { value: GATING_TRIGGER_TYPES.INTERACTIVE_SECTION, label: "Section" };
const DOWNLOAD_OPTION: TriggerOption = { value: GATING_TRIGGER_TYPES.INTERACTIVE_DOWNLOAD, label: "On Download" };
const EXIT_INTENT_OPTION: TriggerOption = { value: GATING_TRIGGER_TYPES.EXIT_INTENT, label: "Exit Intent" };
const SMART_OPTION: TriggerOption = { value: GATING_TRIGGER_TYPES.SMART, label: "Auto trigger" };
const SLIDE_OPTION: TriggerOption = { value: GATING_TRIGGER_TYPES.SPATIAL_SLIDE, label: "Slide" };

export const TRIGGER_TYPES: TriggerOption[] = [
  TIME_OPTION,
  PERCENTAGE_OPTION,
  SECTION_OPTION,
  DOWNLOAD_OPTION,
  EXIT_INTENT_OPTION,
  SMART_OPTION,
];

// Mirrors LANDING_PAGE_TRIGGER.
export const LANDING_PAGE_TRIGGER: TriggerOption[] = [
  TIME_OPTION,
  PERCENTAGE_OPTION,
  EXIT_INTENT_OPTION,
  SMART_OPTION,
];

// Mirrors SPATIAL_TRIGGER.
export const SPATIAL_TRIGGER: TriggerOption[] = [
  SLIDE_OPTION,
  TIME_OPTION,
  EXIT_INTENT_OPTION,
  SMART_OPTION,
];

// Mirrors FLIP_BOOK_TRIGGER (SPATIAL minus SMART and SPATIAL_SLIDE).
const FLIP_BOOK_EXCLUDED_TRIGGERS: GatingTriggerType[] = [
  GATING_TRIGGER_TYPES.SMART,
  GATING_TRIGGER_TYPES.SPATIAL_SLIDE,
];

export const FLIP_BOOK_TRIGGER: TriggerOption[] = SPATIAL_TRIGGER.filter(
  (option) => !FLIP_BOOK_EXCLUDED_TRIGGERS.includes(option.value),
);

// Mirrors ASSET_MAPPING_TRIGGER.
export const ASSET_MAPPING_TRIGGER: TriggerOption[] = [TIME_OPTION, PERCENTAGE_OPTION];


export const isInteractiveContentType = (type?: StoryboardType): boolean =>
  !!type && INTERACTIVE_CONTENT_TYPES.has(type);
  
export const SLIDE_STORYBOARD_TYPES: StoryboardType[] = [
  STORYBOARD_TYPES.SPATIAL,
  STORYBOARD_TYPES.FLIP_BOOK,
];

export const isSlideStoryboardType = (type?: StoryboardType): boolean =>
  !!type && SLIDE_STORYBOARD_TYPES.includes(type);

export const resolveGatingTriggerType = ({
  smart,
  autoGating,
  triggerType,
}: {
  smart?: boolean | null;
  autoGating?: boolean | null;
  triggerType: GatingTriggerType;
}): GatingTriggerType => {
  if (smart) return GATING_TRIGGER_TYPES.SMART;
  if (autoGating) return GATING_TRIGGER_TYPES.AUTO_GATING;
  if (triggerType === GATING_TRIGGER_TYPES.FLIPBOOK_SLIDE) {
    return GATING_TRIGGER_TYPES.SPATIAL_SLIDE;
  }
  return triggerType;
};

export const getTriggerOptions = (
  storyboardType?: StoryboardType,
  context: "page" | "asset" = "page",
): TriggerOption[] => {
  if (context === "asset") return ASSET_MAPPING_TRIGGER;
  if (storyboardType === STORYBOARD_TYPES.FLIP_BOOK) return FLIP_BOOK_TRIGGER;
  if (storyboardType === STORYBOARD_TYPES.SPATIAL) return SPATIAL_TRIGGER;
  if (isInteractiveContentType(storyboardType)) return TRIGGER_TYPES;
  return LANDING_PAGE_TRIGGER;
};

export const TRIGGER_TYPE_OPTIONS: TriggerOption[] = TRIGGER_TYPES;

export const TRIGGER_UNIT_LABEL: Record<GatingTriggerType, string> = {
  TIME: "seconds",
  PERCENTAGE: "%",
  INTERACTIVE_SECTION: "",
  INTERACTIVE_DOWNLOAD: "",
  EXIT_INTENT: "",
  SMART: "",
  AUTO_GATING: "",
  SPATIAL_SLIDE: "",
  FLIPBOOK_SLIDE: "",
};

export const TRIGGERS_WITHOUT_VALUE: GatingTriggerType[] = [
  GATING_TRIGGER_TYPES.INTERACTIVE_DOWNLOAD,
  GATING_TRIGGER_TYPES.EXIT_INTENT,
  GATING_TRIGGER_TYPES.SMART,
  GATING_TRIGGER_TYPES.AUTO_GATING,
  GATING_TRIGGER_TYPES.INTERACTIVE_SECTION,
  GATING_TRIGGER_TYPES.SPATIAL_SLIDE,
  GATING_TRIGGER_TYPES.FLIPBOOK_SLIDE,
];
